(() => {
  'use strict';
  const config = window.SXAvidLicenceConfig;
  const text = JSON.parse(document.getElementById('sx-avid-translations')?.textContent || '{}');
  if (!config || customElements.get('sx-avid-consent')) return;
  const root = window.Shopify?.routes?.root || '/';
  const panels = new Set();
  const editionText = JSON.parse(document.getElementById('sx-cubase-edition-translations')?.textContent || '{}');
  const sourceRequired = profile => profile.editionPicker && ['update', 'upgrade', 'crossgrade'].includes(profile.kind);
  const sourceValid = (profile, source) => {
    if (!sourceRequired(profile)) return true;
    if (!source) return false;
    if (profile.sourceChoices) return profile.sourceChoices.includes(source.licence);
    if (profile.kind === 'crossgrade') return (profile.qualifyingProducts || []).includes(source.daw);
    const version = Number(source.version);
    return source.edition === profile.from.edition && source.version !== '' && version >= profile.from.min && version <= profile.from.max;
  };
  const sourceLabel = source => source?.licence || source?.daw || (source?.edition && source?.version ? `Cubase ${source.edition} ${source.version}` : '');
  const parseSource = value => { try { return JSON.parse(value || 'null'); } catch (_) { return null; } };
  const ownership = (container, profile, onChange) => {
    container.replaceChildren();
    container.className = 'sx-cubase-ownership';
    if (!sourceRequired(profile)) return () => null;
    const fields = {};
    const select = (name, caption, values) => {
      const label = document.createElement('label'); label.append(document.createTextNode(caption));
      const input = document.createElement('select'); input.dataset.sxSource = name; input.setAttribute('aria-label', caption); input.required = true;
      for (const [value, title] of [['', editionText.choose], ...values, ['unknown', editionText.unknown]]) {
        const option = document.createElement('option'); option.value = value; option.textContent = title; input.append(option);
      }
      input.addEventListener('change', onChange); label.append(input); container.append(label); fields[name] = input;
    };
    if (profile.sourceChoices) select('licence', editionText.existing_licence, profile.sourceChoices.map(value => [value, value]));
    else if (profile.kind === 'crossgrade') select('daw', editionText.daw, profile.qualifyingProducts.map(value => [value, value]));
    else {
      select('edition', editionText.edition, ['Pro', 'Artist', 'Elements', 'AI', 'LE'].map(value => [value, `Cubase ${value}`]));
      select('version', editionText.version, [6,6.5,7,7.5,8,8.5,9,9.5,10,10.5,11,12,13,14,15,16].map(value => [String(value), String(value)]));
    }
    return () => Object.fromEntries(Object.entries(fields).map(([key, input]) => [key, input.value]));
  };
  const propertyNames = ['Licence selected', 'Licence eligibility', 'Licence confirmed at', '_sx_licence_version', '_sx_licence_sku', '_sx_licence_variant', '_sx_licence_quantity', 'Existing licence', '_sx_existing_licence'];
  const properties = (profile, sku, variant, quantity, source) => ({
    'Existing licence': sourceLabel(source),
    '_sx_existing_licence': source ? JSON.stringify(source) : '',
    'Licence selected': `${profile.product} — ${profile.label}`,
    'Licence eligibility': profile.ack,
    'Licence confirmed at': new Date().toISOString(),
    '_sx_licence_version': config.version,
    '_sx_licence_sku': sku,
    '_sx_licence_variant': String(variant),
    '_sx_licence_quantity': String(quantity)
  });
  const confirmedProperties = (item, source) => {
    const kept = {...(item.properties || {})};
    delete kept['Digital supply consent'];
    delete kept['Consent recorded at'];
    return {...kept, ...properties(config.profiles[item.sku], item.sku, item.variant_id, item.quantity, source)};
  };
  const valid = item => {
    const profile = config.profiles[item.sku];
    if (!profile) return true;
    const p = item.properties || {};
    return sourceValid(profile, parseSource(p._sx_existing_licence)) && p._sx_licence_version === config.version && p._sx_licence_sku === item.sku && p._sx_licence_variant === String(item.variant_id) && p._sx_licence_quantity === String(item.quantity) && p['Licence eligibility'] === profile.ack && p['Licence selected'] === `${profile.product} — ${profile.label}` && Number.isFinite(Date.parse(p['Licence confirmed at']));
  };
  class AvidConsent extends HTMLElement {
    connectedCallback() {
      if (this.initialised) { panels.add(this); this.update(); return; }
      this.initialised = true;
      this.form = this.closest('form');
      this.requireReady = this.hasAttribute('data-require-ready');
      this.requireEligibility = this.hasAttribute('data-require-eligibility');
      this.getSource = () => null;
      this.pending = false;
      this.variants = JSON.parse(this.querySelector('[data-sx-variants]').textContent);
      this.eligibility = this.querySelector('[data-sx-eligibility]');
      this.heading = this.querySelector('[data-sx-choice]');
      this.summary = this.querySelector('[data-sx-summary]');
      this.ack = this.querySelector('[data-sx-ack]');
      this.error = this.querySelector('[data-sx-error]');
      this.form.dataset.sxAvid = 'ready';
      for (const name of propertyNames) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${name}]`;
        input.dataset.sxConsentProperty = name;
        this.append(input);
      }
      this.addEventListener('change', () => { this.error.textContent = this.requireEligibility && !sourceValid(this.current?.profile || {}, this.getSource()) ? editionText.mismatch : ''; this.lock(); });
      if (this.requireReady) this.form.addEventListener('sx:variant:rendered', () => { this.pending = false; this.lock(); });
      panels.add(this);
      this.update();
    }
    disconnectedCallback() { panels.delete(this); }
    update(id) {
      const variantId = String(id || this.form.elements.namedItem('id')?.value || this.dataset.initialVariant);
      const variant = this.variants.find(v => String(v.id) === variantId);
      const profile = variant && config.profiles[variant.sku];
      if (!profile) {
        this.current = null;
        this.heading.textContent = text.unavailable;
        this.summary.textContent = text.contact;
        this.eligibility.disabled = true;
        this.lock();
        return;
      }
      if (this.current?.id === variantId) { this.lock(); return; }
      this.current = {id: variantId, sku: variant.sku, profile, variant};
      if (this.requireEligibility) this.getSource = ownership(this.querySelector('[data-sx-ownership]'), profile, () => { this.clearConfirmation(); this.lock(); });
      this.eligibility.checked = false;
      this.eligibility.disabled = false;
      this.heading.textContent = this.requireEligibility ? `${profile.targetLabel} — ${profile.label}` : profile.label;
      this.summary.textContent = profile.summary;
      this.ack.textContent = profile.ack;
      this.error.textContent = '';
      this.querySelectorAll('[data-sx-consent-property]').forEach(input => { input.value = ''; });
      this.lock();
    }
    clearConfirmation() {
      this.eligibility.checked = false;
      this.querySelectorAll('[data-sx-consent-property]').forEach(input => { input.value = ''; });
    }
    lock() {
      if (!this.requireReady) return true;
      const variant = this.current?.variant;
      const ready = !!(this.isConnected && variant && !this.current.profile.blocked && !this.pending && variant.available && Number(variant.price) > 0 && String(this.form.elements.namedItem('id')?.value) === this.current.id);
      const eligible = !this.requireEligibility || (this.current && sourceValid(this.current.profile, this.getSource()));
      this.eligibility.disabled = !this.current || this.pending || !eligible;
      const allowed = ready && eligible && this.eligibility.checked;
      this.form.querySelectorAll('[data-product-add-to-cart-button]').forEach(button => { button.disabled = !allowed; });
      return allowed;
    }
    confirm(event) {
      this.update();
      if (!this.current || !this.eligibility.checked || !this.lock()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.error.textContent = text.required;
        this.eligibility.focus();
        return false;
      }
      const quantity = Number(this.form.elements.namedItem('quantity')?.value || 1);
      const values = properties(this.current.profile, this.current.sku, this.current.id, quantity, this.requireEligibility ? this.getSource() : null);
      this.querySelectorAll('[data-sx-consent-property]').forEach(input => { input.value = values[input.dataset.sxConsentProperty]; });
      return true;
    }
  }
  customElements.define('sx-avid-consent', AvidConsent);
  document.addEventListener('variant:change', event => {
    for (const panel of panels) {
      const sourceForm = event.target.closest?.('form');
      if (sourceForm && sourceForm !== panel.form) continue;
      if (typeof event.detail?.formId === 'string' && event.detail.formId !== panel.form.getAttribute('id')) continue;
      const id = event.detail?.variant?.id;
      if (id && panel.variants.some(v => String(v.id) === String(id))) panel.update(id);
    }
  });
  document.addEventListener('change', event => {
    for (const panel of panels) {
      if (!panel.requireReady || event.target.form !== panel.form) continue;
      if (event.target.name?.startsWith('option')) { panel.pending = true; panel.clearConfirmation(); panel.lock(); }
      if (event.target.name === 'quantity') { panel.clearConfirmation(); panel.lock(); }
    }
  }, true);
  window.addEventListener('pageshow', () => {
    for (const panel of panels) if (panel.requireReady) { panel.clearConfirmation(); panel.lock(); }
  });
  document.addEventListener('change', event => {
    if (event.target.name?.startsWith('option')) queueMicrotask(() => panels.forEach(panel => panel.update()));
  });
  window.addEventListener('submit', event => {
    const panel = event.target.querySelector?.('sx-avid-consent');
    if (panel) panel.confirm(event);
  }, true);
  window.addEventListener('click', event => {
    const button = event.target.closest?.('button[type="submit"],input[type="submit"]');
    const panel = button?.form?.querySelector('sx-avid-consent');
    if (panel) panel.confirm(event);
  }, true);
  async function cart() {
    const response = await fetch(`${root}cart.js`, {headers: {'Accept': 'application/json'}, cache: 'no-store'});
    if (!response.ok) throw new Error(text.failed);
    return response.json();
  }
  const node = (tag, className, content) => {
    const element = document.createElement(tag);
    element.className = className;
    if (content) element.textContent = content;
    return element;
  };
  async function ask(items) {
    return new Promise(resolve => {
      const dialog = node('dialog', 'sx-avid-dialog');
      const heading = node('h2', '', text.cart_heading);
      heading.id = 'sx-avid-cart-title';
      dialog.setAttribute('aria-labelledby', heading.id);
      const form = node('form', 'sx-avid-dialog__body');
      form.append(heading, node('p', '', text.cart_intro));
      const sources = new Map();
      for (const item of items) {
        const profile = config.profiles[item.sku];
        const section = node('section', 'sx-avid-dialog__item');
        section.append(node('h3', '', `${profile.product} — ${profile.label} × ${item.quantity}`), node('p', '', profile.summary));
        const label = node('label', 'sx-avid-check');
        const input = document.createElement('input');
        input.type = 'checkbox'; input.required = true;
        label.append(input, node('span', '', profile.ack));
        if (sourceRequired(profile)) {
          const container = node('div', 'sx-cubase-ownership');
          let getSource;
          getSource = ownership(container, profile, () => { input.checked = false; input.disabled = !sourceValid(profile, getSource()); });
          input.disabled = true; sources.set(item.key, getSource); section.append(container);
        }
        section.append(label); form.append(section);
      }
      const error = node('p', 'sx-avid-error'); error.setAttribute('role', 'alert');
      const actions = node('div', 'sx-avid-dialog__actions');
      const back = node('button', 'sx-avid-secondary', text.back); back.type = 'button';
      const next = node('button', 'sx-avid-primary', text.continue); next.type = 'submit';
      actions.append(back, next); form.append(error, actions); dialog.append(form); document.body.append(dialog);
      let settled = false;
      const finish = value => { if (settled) return; settled = true; dialog.close(); dialog.remove(); resolve(value); };
      back.addEventListener('click', () => finish(false));
      dialog.addEventListener('cancel', event => { event.preventDefault(); finish(false); });
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (!form.reportValidity()) return;
        if (items.some(item => !sourceValid(config.profiles[item.sku], sources.get(item.key)?.()))) { error.textContent = editionText.mismatch; return; }
        next.disabled = true;
        try {
          for (const item of items) {
            const response = await fetch(`${root}cart/change.js`, {method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({id:item.key,quantity:item.quantity,properties:confirmedProperties(item, sources.get(item.key)?.())})});
            if (!response.ok) throw new Error(text.failed);
          }
          const checked = await cart();
          if (checked.items.some(item => !valid(item))) throw new Error(text.changed);
          finish(true);
        } catch (reason) { error.textContent = reason.message || text.failed; next.disabled = false; }
      });
      dialog.showModal();
    });
  }
  let checking = false;
  async function checkout(event) {
    event.preventDefault(); event.stopImmediatePropagation();
    if (checking) return;
    checking = true;
    try {
      const data = await cart();
      if (data.items.some(item => config.profiles[item.sku]?.blocked || (config.profiles[item.sku]?.guided && Number(item.original_price) <= 0))) throw new Error(text.failed);
      const missing = data.items.filter(item => !valid(item));
      if (!missing.length || await ask(missing)) location.assign(`${root}checkout`);
    } catch (_) { window.alert(text.failed); }
    finally { checking = false; }
  }
  window.addEventListener('click', event => {
    const target = event.target.closest?.('a[href],button[name="checkout"],input[name="checkout"],.cart__checkout-button,.checkout-button');
    if (!target) return;
    const href = target.getAttribute('href');
    if (href) { const url = new URL(href, location.href); if (url.origin !== location.origin || !/\/checkout\/?$/.test(url.pathname)) return; }
    if (target.closest('.sx-avid-dialog')) return;
    checkout(event);
  }, true);
  window.addEventListener('submit', event => {
    const form = event.target;
    if (form.closest('.sx-avid-dialog') || form.querySelector('sx-avid-consent')) return;
    if (/\/cart\/?$/.test(new URL(form.action, location.href).pathname) && (event.submitter?.name === 'checkout' || form.querySelector('input[type="hidden"][name="checkout"]'))) checkout(event);
  }, true);
})();


